import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** 当前进行中的 section 请求，避免预取和正式请求互相打断 */
  /** @type {string | undefined} */
  #inflightUrl;

  /** @type {AbortController | undefined} */
  #prefetchAbort;

  /** @type {number} */
  #prefetchTimer = 0;

  /** @type {Map<string, string>} */
  #responseCache = new Map();

  /** @type {((event: Event) => void) | null} */
  #boundVariantChanged = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#boundVariantChanged) this.#boundVariantChanged = this.variantChanged.bind(this);
    this.addEventListener('change', this.#boundVariantChanged);
    this.addEventListener('pointerenter', this.#onPointerEnter, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#boundVariantChanged) this.removeEventListener('change', this.#boundVariantChanged);
    this.removeEventListener('pointerenter', this.#onPointerEnter, true);
    this.#abortController?.abort();
    this.#prefetchAbort?.abort();
    window.clearTimeout(this.#prefetchTimer);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
    // 图已经在画廊里：先切过去，不用等 section 回来
    this.#previewVariantMedia(selectedOption.dataset.mediaId, selectedOption.dataset.variantId);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, [], { loadsNewProduct }), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {{ loadsNewProduct?: boolean, persistPending?: boolean }} [flags]
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], flags = {}) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (flags.persistPending !== false) {
      this.#pendingRequestUrl = productUrl;
    }
    const params = [];

    if (source === 'prefetch' && sourceSelectedOptionsValues.length) {
      params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const baseUrl = (productUrl || '').split('?')[0];

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      return `${baseUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // 换商品（combined listing）仍拉整页；同商品只拉 product section，避免整页 HTML
    const sectionId = this.dataset.sectionId;
    if (!flags.loadsNewProduct && sectionId) {
      return `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&${params.join('&')}`;
    }
    return `${baseUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#inflightUrl = requestUrl;

    const cached = this.#readCache(requestUrl);
    if (cached) {
      this.#applyResponse(cached, shouldMorphMain);
      return;
    }

    fetch(requestUrl, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#writeCache(requestUrl, responseText);
        if (signal.aborted) return;
        this.#applyResponse(responseText, shouldMorphMain);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  /**
   * @param {string} responseText
   * @param {boolean} shouldMorphMain
   */
  #applyResponse(responseText, shouldMorphMain) {
    this.#pendingRequestUrl = undefined;
    this.#inflightUrl = undefined;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) return;

    if (shouldMorphMain) {
      this.updateMain(html);
      return;
    }

    const newProduct = this.updateVariantPicker(html);
    if (!this.selectedOptionId) return;

    this.dispatchEvent(
      new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
        html,
        productId: this.dataset.productId ?? '',
        newProduct,
      })
    );
  }

  /** @param {string} url */
  #readCache(url) {
    return this.#responseCache.get(url);
  }

  /**
   * @param {string} url
   * @param {string} text
   */
  #writeCache(url, text) {
    if (!url || !text) return;
    this.#responseCache.delete(url);
    this.#responseCache.set(url, text);
    while (this.#responseCache.size > 8) {
      const oldest = this.#responseCache.keys().next().value;
      if (!oldest) break;
      this.#responseCache.delete(oldest);
    }
  }

  /** 悬停预取相邻变体，仅鼠标/笔，避免触摸误打请求 */
  #onPointerEnter = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (this.closest('product-card, quick-add-component, swatches-variant-picker-component')) return;

    const option = event.target instanceof Element
      ? event.target.closest('[data-option-value-id]')
      : null;
    if (!(option instanceof HTMLElement)) return;
    if (option instanceof HTMLInputElement && option.checked) return;
    if (option instanceof HTMLOptionElement && option.selected) return;

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const connectedUrl = option.dataset.connectedProductUrl;
    if (connectedUrl && currentUrl && connectedUrl !== currentUrl) return;

    window.clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = window.setTimeout(() => {
      const values = this.#optionValuesIfSelecting(option);
      if (!values.length) return;
      const url = this.buildRequestUrl(option, 'prefetch', values, { persistPending: false });
      if (!url || this.#readCache(url) || url === this.#inflightUrl) return;

      this.#prefetchAbort?.abort();
      const controller = new AbortController();
      this.#prefetchAbort = controller;
      fetch(url, { signal: controller.signal })
        .then((response) => response.text())
        .then((text) => {
          if (controller.signal.aborted) return;
          this.#writeCache(url, text);
        })
        .catch(() => {});
    }, 80);
  };

  /**
   * 假设点了该选项后的 option_values（其它轴保持当前选中）
   * @param {HTMLElement} optionEl
   * @returns {string[]}
   */
  #optionValuesIfSelecting(optionEl) {
    const nextId = optionEl.dataset.optionValueId;
    if (!nextId) return [];
    const fieldset = optionEl.closest('fieldset');
    const select = optionEl.closest('select');
    /** @type {string[]} */
    const values = [];

    this.querySelectorAll('fieldset').forEach((fs) => {
      if (fieldset && fs === fieldset) {
        values.push(nextId);
        return;
      }
      const checked = fs.querySelector('input:checked');
      const id = checked instanceof HTMLElement ? checked.dataset.optionValueId : '';
      if (id) values.push(id);
    });

    this.querySelectorAll('select').forEach((sel) => {
      if (select && sel === select) {
        values.push(nextId);
        return;
      }
      const selected = sel.querySelector('option[selected]') || sel.selectedOptions?.[0];
      const id = selected instanceof HTMLElement ? selected.dataset.optionValueId : '';
      if (id) values.push(id);
    });

    return values;
  }

  /**
   * @param {string | undefined} mediaId
   * @param {string | undefined} variantId
   */
  #previewVariantMedia(mediaId, variantId) {
    const root = this.closest('.shopify-section, dialog');
    const gallery = root?.querySelector('media-gallery');
    if (gallery && typeof gallery.previewMedia === 'function') {
      gallery.previewMedia(mediaId, variantId);
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
